<template>
    <!-- bo-api 099514b1 / #931: an admin attaches the barcode the carrier already
         issued for a barcode_not_saved order (PUT /orders/tracking-attach/:id). -->
    <el-dialog
        :visible="visible"
        :title="$t('tracking_failure.attach_title', { id: orderId })"
        :append-to-body="true"
        :close-on-click-modal="false"
        width="480px"
        custom-class="bo-attach-tracking-dialog"
        @update:visible="v => $emit('update:visible', v)"
    >
        <div data-testid="attach-tracking">
            <div v-if="message" class="alert alert-warning py-2 px-3 mb-3 at-msg" data-testid="at-carrier-message">{{ message }}</div>
            <label class="form-control-label" for="at-tn">{{ $t("tracking_failure.attach_label") }}</label>
            <el-input
                id="at-tn"
                v-model="trackingNumber"
                maxlength="45"
                :class="{ 'at-invalid': !!shownError }"
                data-testid="at-tracking-number"
                @blur="touched = true"
            ></el-input>
            <div class="text-muted at-hint mt-1">{{ $t("tracking_failure.attach_hint") }}</div>
            <div v-if="shownError" class="at-err mt-1" data-testid="at-error">{{ $t(shownError) }}</div>
            <div v-if="serverError" class="alert alert-danger py-2 px-3 mt-3 mb-0" data-testid="at-server-error">{{ serverError }}</div>
        </div>
        <span slot="footer">
            <base-button type="secondary" :disabled="saving" @click="$emit('update:visible', false)">{{ $t("cancel") }}</base-button>
            <base-button type="primary" :disabled="saving" data-testid="at-save" @click="save">
                <i v-if="saving" class="fa fa-spinner fa-spin mr-1"></i>{{ $t("save") }}
            </base-button>
        </span>
    </el-dialog>
</template>

<script>
import { Dialog, Input } from "element-ui";
import { OrderService } from "@/resource";
import { packingRoundErrorKey } from "@/utils/sellerPackingRound";
import {
    attachErrorKey,
    barcodeFromMessage,
    normalizeTrackingNumber,
    orderIdOf,
    trackingNumberError
} from "@/utils/trackingFailure";

export default {
    name: "AttachTrackingDialog",
    components: {
        [Dialog.name]: Dialog,
        [Input.name]: Input
    },
    props: {
        visible: { type: Boolean, default: false },
        // A tracking-failure row (bo_order_id, carrier_message).
        row: { type: Object, default: null }
    },
    data() {
        return { trackingNumber: "", touched: false, saving: false, serverError: "" };
    },
    computed: {
        orderId() {
            return orderIdOf(this.row);
        },
        message() {
            return (this.row && this.row.carrier_message) || "";
        },
        error() {
            return trackingNumberError(this.trackingNumber);
        },
        shownError() {
            return this.touched ? this.error : "";
        }
    },
    watch: {
        visible: {
            immediate: true,
            handler(v) {
                if (!v) return;
                this.trackingNumber = barcodeFromMessage(this.message);
                this.touched = false;
                this.serverError = "";
            }
        }
    },
    methods: {
        async save() {
            this.touched = true;
            if (this.error || !this.orderId) return;
            this.saving = true;
            this.serverError = "";
            try {
                const tn = normalizeTrackingNumber(this.trackingNumber);
                await OrderService.attachTracking(this.orderId, { tracking_number: tn });
                this.$notify({ type: "success", message: this.$t("tracking_failure.attach_saved", { tn }) });
                this.$emit("saved", { id: this.orderId, tracking_number: tn });
                this.$emit("update:visible", false);
            } catch (err) {
                const key = attachErrorKey(packingRoundErrorKey(err));
                this.serverError = key && this.$te(key) ? this.$t(key) : ((err && err.message) || this.$t("errors.internal"));
            } finally {
                this.saving = false;
            }
        }
    }
};
</script>

<style>
.bo-attach-tracking-dialog .at-msg {
    font-family: SFMono-Regular, Menlo, Consolas, monospace;
    word-break: break-word;
}
.bo-attach-tracking-dialog .at-hint {
    font-size: 0.8125rem;
}
.bo-attach-tracking-dialog .at-err {
    color: #c0392b;
    font-size: 0.8125rem;
}
.bo-attach-tracking-dialog .at-invalid .el-input__inner {
    border-color: #c0392b;
}
</style>
